import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { devBypassEnabled } from '../auth/dev-session';
import { firebaseConfigured } from '../auth/firebase';
import { ROLE_DESCRIPTION, ROLE_LABEL, STAFF_ROLES, StaffRole } from '../auth/roles';
import { Field } from '../components/ui/Field';

/** Admin console sign-in with Google; access is granted per email by an Admin (Users & access). */
export default function Login() {
  const { session, signInWithGoogle, devLogin, error } = useAuth();
  const loc = useLocation() as { state?: { from?: string } };
  const [busy, setBusy] = useState(false);
  const [devRole, setDevRole] = useState<StaffRole>('admin');
  const [devUser, setDevUser] = useState('00000000-0000-4000-8000-000000000001');

  if (session) return <Navigate to={loc.state?.from ?? '/'} replace />;

  const google = async () => {
    setBusy(true);
    try {
      await signInWithGoogle();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="card">
        <div>
          <img src="/logo.png" alt="Mera Diya" width={88} height={88} style={{ borderRadius: '50%', marginBottom: 8 }} />
          <h1>Mera Diya admin</h1>
          <p className="lead">For people with admin console access: Admin, Manager or Viewer.</p>
        </div>
        {error && <div className="state error" style={{ padding: 0, textAlign: 'left' }} role="alert">{error}</div>}

        {firebaseConfigured ? (
          <button className="btn google" onClick={google} disabled={busy}>
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
              <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
            </svg>
            {busy ? 'Signing in…' : 'Sign in with Google'}
          </button>
        ) : (
          !devBypassEnabled && <p className="lead">Firebase is not configured. Set VITE_FIREBASE_* in the environment.</p>
        )}

        {devBypassEnabled && (
          <>
            <div className="divider">development only</div>
            <form
              onSubmit={(e) => { e.preventDefault(); devLogin(devRole, devUser.trim()); }}
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <Field label="Sign in as role" hint={ROLE_DESCRIPTION[devRole]}>
                <select className="select" value={devRole} onChange={(e) => setDevRole(e.target.value as StaffRole)}>
                  {STAFF_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              </Field>
              <Field label="Console user id">
                <input className="input" value={devUser} onChange={(e) => setDevUser(e.target.value)} />
              </Field>
              <button className="btn">Dev sign in (bypasses Firebase)</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
